import React from 'react';
import { useNavigate } from 'react-router-dom';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ComputerIcon from '@mui/icons-material/Computer';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Grid, Link, Skeleton, Stack, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import {
  EmptyHint,
  PagedWidgetList,
  StatTile,
  Widget,
  WidgetRow,
} from 'components/dashboard/DashboardWidgets';
import DomainHero, { HERO_PALETTES } from 'components/dashboard/DomainHero';
import { useDashboardProfile } from 'pages/useDashboardProfile';
import { AssessmentListResult, listAssessment } from 'stores/AssessmentStoreAdapter';
import { clampPage } from 'utils/UserProfileGuard';

const ACCENT = '#283593';

const assetLabel = (a: AssessmentListResult) => {
  const nums = [a.managementNumber1, a.managementNumber2, a.managementNumber3].filter(Boolean);
  return nums.length > 0 ? nums.join(' / ') : a.assessmentTypeName;
};

/**
 * 資産ドメインのトップ（ヒーロー + ウィジェットダッシュボード）。
 * 「自分が利用者／管理者の資産」を最優先に据えた自分ごとダッシュボード。
 * - 自分が利用している資産（最優先）
 * - 自分が管理している資産
 * - KPI（総資産 / PC / 自分の利用 / 自分の管理）
 * - 機能ナビ（資産管理）
 */
const AssessmentHome: React.FC = () => {
  const navigate = useNavigate();
  const { userSession } = useAuth();
  const { userId } = userSession;

  // ID指定の詳細は必ず別タブで開く
  const openAssessmentInNewTab = (assessmentId: string) => {
    window.open(
      `/assessment/assessment-management/${assessmentId}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  const [assessments, setAssessments] = React.useState<AssessmentListResult[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);

  // ダッシュボードのリストページ状態を user_profile(AUTO) で保存/復元する
  const [dashboard, setDashboard] = useDashboardProfile('assessment-home-dashboard', {
    myUsedPage: 0,
    myManagedPage: 0,
  });
  const setMyUsedPage = React.useCallback(
    (p: number) => setDashboard((prev) => ({ ...prev, myUsedPage: p })),
    [setDashboard],
  );
  const setMyManagedPage = React.useCallback(
    (p: number) => setDashboard((prev) => ({ ...prev, myManagedPage: p })),
    [setDashboard],
  );

  // ウィジェットの元データ（資産一覧）を取得する。初回ロードと更新ボタンで共用。
  const reload = React.useCallback(async () => {
    try {
      const list = await listAssessment({}).catch((): AssessmentListResult[] => []);
      setAssessments(list);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  // ヒーローの更新ボタン: 再取得中はスピナー表示にする
  const handleRefresh = React.useCallback(async () => {
    setRefreshing(true);
    try {
      await reload();
    } finally {
      setRefreshing(false);
    }
  }, [reload]);

  const myUsed = React.useMemo(
    () => assessments.filter((a) => a.userId === userId),
    [assessments, userId],
  );
  const myManaged = React.useMemo(
    () => assessments.filter((a) => a.managerId === userId),
    [assessments, userId],
  );
  const pcCount = assessments.filter((a) => a.assessmentTypeCode === 'PC').length;

  return (
    <Box sx={{ height: '100%', overflow: 'auto', pr: 1 }}>
      <Stack spacing={2.5} sx={{ maxWidth: 1100 }}>
        {/* ヒーロー */}
        <DomainHero
          eyebrow="Asset Management"
          title="Know Your Assets"
          tagline="See the devices you use and the assets you're responsible for, and keep the whole inventory organized."
          palette={HERO_PALETTES.assessment}
          onBack={() => navigate('/')}
          onRefresh={handleRefresh}
          refreshing={refreshing}
        />

        {/* KPI ストリップ（最上部） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<Inventory2OutlinedIcon sx={{ fontSize: 16 }} />}
              label="登録資産"
              value={assessments.length}
              suffix="件"
              color={ACCENT}
              loading={loading}
              onClick={() => navigate('/assessment/assessment-management')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<ComputerIcon sx={{ fontSize: 16 }} />}
              label="PC"
              value={pcCount}
              suffix="台"
              color="#1565c0"
              loading={loading}
              onClick={() => navigate('/assessment/assessment-management')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<PersonOutlineIcon sx={{ fontSize: 16 }} />}
              label="自分の利用"
              value={myUsed.length}
              suffix="件"
              color="#00838f"
              loading={loading}
              onClick={() => navigate('/assessment/assessment-management')}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <StatTile
              icon={<ManageAccountsOutlinedIcon sx={{ fontSize: 16 }} />}
              label="自分の管理"
              value={myManaged.length}
              suffix="件"
              color="#6a1b9a"
              loading={loading}
              onClick={() => navigate('/assessment/assessment-management')}
            />
          </Grid>
        </Grid>

        {/* 自分の資産（2カラム） */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title={`自分が利用している資産${loading ? '' : `（${myUsed.length}）`}`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/assessment/assessment-management')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  資産管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : myUsed.length === 0 ? (
                <EmptyHint text="自分が利用者の資産はありません" />
              ) : (
                <PagedWidgetList
                  items={myUsed}
                  keyOf={(a) => a.assessmentId}
                  page={clampPage(dashboard.myUsedPage + 1, Math.ceil(myUsed.length / 6)) - 1}
                  onPageChange={setMyUsedPage}
                  renderItem={(a) => (
                    <WidgetRow
                      accentColor={ACCENT}
                      onClick={() => openAssessmentInNewTab(a.assessmentId)}
                    >
                      {a.assessmentTypeCode === 'PC' ? (
                        <ComputerIcon
                          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                        />
                      ) : (
                        <DevicesOtherIcon
                          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {a.assessmentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {assetLabel(a)}
                        </Typography>
                      </Box>
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Widget
              title={`自分が管理している資産${loading ? '' : `（${myManaged.length}）`}`}
              action={
                <Link
                  component="button"
                  variant="caption"
                  underline="hover"
                  onClick={() => navigate('/assessment/assessment-management')}
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  資産管理
                  <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </Link>
              }
            >
              {loading ? (
                <Stack spacing={1}>
                  {[0, 1, 2].map((i) => (
                    <Skeleton key={i} variant="rounded" height={44} />
                  ))}
                </Stack>
              ) : myManaged.length === 0 ? (
                <EmptyHint text="自分が管理者の資産はありません" />
              ) : (
                <PagedWidgetList
                  items={myManaged}
                  keyOf={(a) => a.assessmentId}
                  page={clampPage(dashboard.myManagedPage + 1, Math.ceil(myManaged.length / 6)) - 1}
                  onPageChange={setMyManagedPage}
                  renderItem={(a) => (
                    <WidgetRow
                      accentColor={ACCENT}
                      onClick={() => openAssessmentInNewTab(a.assessmentId)}
                    >
                      {a.assessmentTypeCode === 'PC' ? (
                        <ComputerIcon
                          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                        />
                      ) : (
                        <DevicesOtherIcon
                          sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {a.assessmentName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          利用者: {a.userName || '—'}
                        </Typography>
                      </Box>
                    </WidgetRow>
                  )}
                />
              )}
            </Widget>
          </Grid>
        </Grid>

        {!loading && assessments.length === 0 && <EmptyHint text="登録された資産がありません。" />}
      </Stack>
    </Box>
  );
};

export default AssessmentHome;
